import {useAddNotification} from '@logistics-frontend/blocks'
import {useDebounce} from '@logistics-frontend/hooks'
import {filter, map, pipe} from '@logistics-frontend/utils'
import {useFormContext} from '@yandex-delivery/form'
import {useEffect, useMemo, useRef} from 'react'

import {i18n} from 'src/shared/config'
import {DeliveryInterval, DestinationPointDeliveryMethod, Order, SourcePointDeliveryMethod} from 'src/shared/types'

import {useFetchNddDeliveryIntervals} from './use-fetch-ndd-delivery-intervals'
import {useParcelPhysicalDimensions} from './use-parcel-physical-dimensions'
import {useSourcePointDropOffPointId} from './use-source-point-drop-off-point-id'
import {useSourcePointStationId} from './use-source-point-station-id'
import {useSourcePointSupplyPlan} from './use-source-point-supply-plan'
import {
  isValidDeliveryInterval,
  mapDeliveryIntervals,
  withCorrectDeliveryDate,
  withCorrectDeliveryInterval,
} from './utils'

const setFieldOptions = {
  shouldDirty: false,
  shouldTouch: false,
  shouldValidate: false,
}

export function useNddDeliveryIntervals() {
  const {
    isDeliveryIntervalsPending,
    deliveryIntervals = [],
    fetchDeliveryIntervalsHandler,
    error,
  } = useFetchNddDeliveryIntervals()
  const prevDeliveryDate = useRef<string>()
  const {watch, clearErrors, setFieldValue, setFieldError} = useFormContext<Order>()
  const {places, totalSize} = useParcelPhysicalDimensions()
  const sourcePointStationId = useSourcePointStationId()
  const sourcePointDropOffPointId = useSourcePointDropOffPointId()
  const sourcePointSupplyPlan = useSourcePointSupplyPlan()
  const addNotification = useAddNotification()

  const sourcePointDeliveryMethod = watch('sourcePoint._deliveryMethod')
  const destinationPointDeliveryMethod = watch('destinationPoints.0._deliveryMethod')
  const destinationPointPickUpPointId = watch('destinationPoints.0.pickUpPoint.point.id')
  const dateValue = watch('ndd.deliveryDate')
  const destinationPointDeliveryAddress = watch('destinationPoints.0.address.fullName')

  const sourcePointId =
    sourcePointDeliveryMethod === SourcePointDeliveryMethod.Station ? sourcePointStationId : sourcePointDropOffPointId

  const isEmptyDestinationPoint = !destinationPointDeliveryAddress && !destinationPointPickUpPointId

  const deliveryIntervalsMap = useMemo(() => {
    if (deliveryIntervals.length === 0) {
      return {}
    }

    return pipe(deliveryIntervals, filter(isValidDeliveryInterval), mapDeliveryIntervals)
  }, [deliveryIntervals])

  const dateList = useMemo<Array<string>>(() => {
    if (deliveryIntervals.length === 0) {
      return []
    }

    return deliveryIntervalsMap ? Object.keys(deliveryIntervalsMap) : []
  }, [deliveryIntervals, deliveryIntervalsMap])

  const intervalList = useMemo<Array<DeliveryInterval>>(() => {
    if (!dateValue || !deliveryIntervalsMap) return []

    const intervals = deliveryIntervalsMap[dateValue]
    return intervals || []
  }, [dateValue, deliveryIntervalsMap])

  const firstInterval = useMemo(() => {
    return dateValue && deliveryIntervalsMap ? deliveryIntervalsMap[dateValue]?.[0] : undefined
  }, [dateValue, deliveryIntervalsMap])

  const preparedDeliveryIntervals = useMemo(() => {
    if (deliveryIntervals.length === 0) {
      return []
    }

    return pipe(
      deliveryIntervals,
      filter(isValidDeliveryInterval),
      map(deliveryInterval => ({
        from: deliveryInterval.from.toString(),
        to: deliveryInterval.to.toString(),
      })),
    )
  }, [deliveryIntervals])

  /**
   * @BusinessLogic
   * При изменении даты доставки необходимо обновить выбранный интервал доставки
   */
  useEffect(() => {
    if (prevDeliveryDate.current === dateValue) return

    if (destinationPointDeliveryMethod === DestinationPointDeliveryMethod.Address) {
      setFieldValue('ndd.deliveryInterval', firstInterval, setFieldOptions)
    }

    prevDeliveryDate.current = dateValue
  }, [dateValue, destinationPointDeliveryMethod, firstInterval, setFieldValue])

  /**
   * @BusinessLogic
   * Если пришла ошибка при запросе интервалов доставки или интервалы доставки отсутствуют
   * необходимо установить ошибки в поля Откуда и Куда и обнулить значения интервалов доставки
   */
  useEffect(() => {
    if (error) {
      setFieldError(
        'destinationPoints.0.pickUpPoint.point',
        i18n('ndd.offers.info.error', 'К сожалению, доставка по указанному адресу недоступна'),
      )
      setFieldError(
        'sourcePoint.dropOffPoint.point',
        i18n('ndd.offers.info.error', 'К сожалению, доставка по указанному адресу недоступна'),
      )

      setFieldValue('ndd.__deliveryIntervalsMap', {}, setFieldOptions)
      setFieldValue('ndd.__deliveryIntervalsRaw', [], setFieldOptions)
      setFieldValue('ndd.deliveryDate', undefined, setFieldOptions)
      setFieldValue('ndd.deliveryInterval', undefined, setFieldOptions)

      return
    }

    clearErrors('sourcePoint.dropOffPoint.point')
    clearErrors('destinationPoints.0.pickUpPoint.point')
  }, [clearErrors, error, setFieldError, setFieldValue])

  /**
   * @BusinessLogic
   * При изменении адреса или пункта самовывоза, а также при изменении типа доставки
   * необходимо обновить список доступных интервалов доставки прежде обнулив предыдущие значения
   */
  useDebounce(
    () => {
      if (isEmptyDestinationPoint || !sourcePointId || totalSize === 0) {
        return
      }

      if (sourcePointDeliveryMethod === SourcePointDeliveryMethod.Station && sourcePointSupplyPlan === undefined) {
        addNotification(i18n('ndd.create_order.no_offers_error'), {
          type: 'error',
        })
        return
      }

      switch (destinationPointDeliveryMethod) {
        case DestinationPointDeliveryMethod.Address: {
          if (destinationPointDeliveryAddress) {
            fetchDeliveryIntervalsHandler({
              full_address: destinationPointDeliveryAddress,
              station_id: sourcePointId,
              places,
            })
          }
          break
        }
        case DestinationPointDeliveryMethod.PickUpPoint: {
          if (destinationPointPickUpPointId) {
            fetchDeliveryIntervalsHandler({
              self_pickup_id: destinationPointPickUpPointId,
              station_id: sourcePointId,
              places,
            })
          }
          break
        }
      }

      setFieldValue('ndd.__deliveryIntervalsMap', deliveryIntervalsMap, setFieldOptions)
      setFieldValue('ndd.__deliveryIntervalsRaw', preparedDeliveryIntervals, setFieldOptions)
      setFieldValue('ndd.deliveryDate', withCorrectDeliveryDate(preparedDeliveryIntervals), setFieldOptions)
      setFieldValue('ndd.deliveryInterval', withCorrectDeliveryInterval(preparedDeliveryIntervals), setFieldOptions)
    },
    300,
    [
      isEmptyDestinationPoint,
      clearErrors,
      sourcePointId,
      sourcePointDeliveryMethod,
      sourcePointSupplyPlan,
      totalSize,
      destinationPointDeliveryMethod,
      destinationPointDeliveryAddress,
      destinationPointPickUpPointId,
    ],
  )

  return {
    isDeliveryIntervalsPending,
    dateList,
    intervalList,
    isDeliveryIntervalsVisible: destinationPointDeliveryMethod === DestinationPointDeliveryMethod.Address,
  }
}
